import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Truck, Package, Tag, X, CheckCircle2, Receipt } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Checkout = ({ cart, onClearCart }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    shipping_address: '',
    shipping_city: '',
    shipping_state: '',
    shipping_pincode: '',
  });
  const [shippingRates, setShippingRates] = useState([]);
  const [selectedShipping, setSelectedShipping] = useState(null);
  const [loadingRates, setLoadingRates] = useState(false);
  const [loading, setLoading] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState(null); // { code, discount, free_shipping, coupon_id }

  const [paymentMethod, setPaymentMethod] = useState('razorpay'); // 'razorpay' | 'cod'

  const subtotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);

  // Calculate total weight in grams. Supports ml / L / g / kg / pcs / drops variants.
  const totalWeight = cart.reduce((sum, item) => {
    const sz = item.selectedSize || '';
    // Extract first number and unit
    const m = String(sz).match(/^\s*([\d.]+)\s*([a-zA-Z]*)\s*$/);
    const num = m ? parseFloat(m[1]) : 10;
    const unit = (m && m[2]) ? m[2].toLowerCase() : 'ml';
    const gramsPerUnit = {
      ml: 1, l: 1000, g: 1, kg: 1000,
      pcs: 50,    // assume ~50 g per packaged piece
      drops: 0.05,
    };
    const grams = (num || 0) * (gramsPerUnit[unit] || 1);
    return sum + grams * item.quantity;
  }, 0);

  const rawShippingCost = selectedShipping ? selectedShipping.rate : 0;
  const isPickup = paymentMethod === 'pickup';
  const shippingCost = isPickup ? 0 : (appliedCoupon?.free_shipping ? 0 : rawShippingCost);
  const discount = appliedCoupon ? Math.min(appliedCoupon.discount, subtotal) : 0;
  const total = Math.max(0, subtotal - discount + shippingCost);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const fetchShippingRates = async (pincode) => {
    if (pincode.length === 6) {
      setLoadingRates(true);
      try {
        const response = await axios.post(`${API}/shipping/calculate-rates`, {
          destination_pincode: pincode,
          weight_grams: totalWeight,
          cod_amount: 0
        });
        
        if (response.data.success && response.data.rates.length > 0) {
          setShippingRates(response.data.rates);
          // Auto-select cheapest option
          const cheapest = response.data.rates.reduce((prev, curr) => 
            prev.rate < curr.rate ? prev : curr
          );
          setSelectedShipping(cheapest);
        }
      } catch (error) {
        console.error('Error fetching shipping rates:', error);
        // Use fallback rate
        setShippingRates([{
          carrier: 'Standard Shipping',
          service_type: 'Surface',
          rate: subtotal > 1000 ? 0 : 100,
          estimated_days: 7,
          courier_id: 'standard'
        }]);
        setSelectedShipping(shippingRates[0]);
      } finally {
        setLoadingRates(false);
      }
    }
  };

  useEffect(() => {
    if (formData.shipping_pincode.length === 6) {
      fetchShippingRates(formData.shipping_pincode);
    }
  }, [formData.shipping_pincode]);

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      toast.error('Enter a coupon code');
      return;
    }
    if (!formData.customer_email) {
      toast.error('Please enter your email before applying a coupon');
      return;
    }
    setCouponLoading(true);
    try {
      const { data } = await axios.post(`${API}/coupons/validate`, {
        code: couponCode.trim(),
        order_value: subtotal,
        customer_email: formData.customer_email.trim(),
      });
      setAppliedCoupon(data);
      toast.success(`Coupon ${data.code} applied!`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Invalid coupon');
      setAppliedCoupon(null);
    } finally {
      setCouponLoading(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
    toast.info('Coupon removed');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Strict mandatory-field validation (required for invoicing & order updates)
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRe = /^[6-9]\d{9}$/;
    if (!formData.customer_name.trim()) { toast.error('Please enter your full name'); return; }
    if (!emailRe.test(formData.customer_email.trim())) { toast.error('Please enter a valid email — we use it to send order updates'); return; }
    if (!phoneRe.test(formData.customer_phone.replace(/\s+/g, ''))) { toast.error('Please enter a valid 10-digit Indian mobile number'); return; }
    if (!formData.shipping_address.trim()) { toast.error('Please enter your shipping address'); return; }
    if (!formData.shipping_city.trim() || !formData.shipping_state.trim()) { toast.error('Please enter city and state'); return; }
    if (!/^\d{6}$/.test(formData.shipping_pincode)) { toast.error('Please enter a valid 6-digit pincode'); return; }

    if (!selectedShipping && paymentMethod !== 'pickup') {
      toast.error('Please wait for shipping rates to load');
      return;
    }

    setLoading(true);

    try {
      const orderData = {
        items: cart.map(item => ({
          product_id: item.id,
          name: item.displayName || item.name,
          price: item.price,
          quantity: item.quantity,
        })),
        total_amount: total,
        shipping_cost: shippingCost,
        shipping_method: paymentMethod === 'pickup' ? 'In-Person Pickup' : selectedShipping.carrier,
        coupon_code: appliedCoupon?.code || null,
        discount_amount: discount,
        payment_method: paymentMethod,
        ...formData,
      };

      const orderResponse = await axios.post(`${API}/orders`, orderData);
      const order = orderResponse.data;

      // COD + In-Person Pickup flow — skip Razorpay entirely
      if (paymentMethod === 'cod' || paymentMethod === 'pickup') {
        if (appliedCoupon?.coupon_id) {
          try {
            await axios.post(`${API}/coupons/${appliedCoupon.coupon_id}/use`, {
              customer_email: formData.customer_email,
            });
          } catch (_) { /* non-blocking */ }
        }
        const msg = paymentMethod === 'cod'
          ? 'Order placed! Pay cash when the courier delivers.'
          : 'Order placed! We will contact you to arrange pickup / local delivery.';
        toast.success(msg);
        onClearCart();
        navigate(`/track-order?order_id=${order.id}&email=${encodeURIComponent(formData.customer_email)}`);
        return;
      }

      const razorpayOrderResponse = await axios.post(`${API}/orders/create-razorpay-order`, {
        amount: total,
        currency: 'INR',
      });

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID || '',
        amount: razorpayOrderResponse.data.amount,
        currency: razorpayOrderResponse.data.currency,
        order_id: razorpayOrderResponse.data.id,
        name: 'Krishna Sugandh',
        description: 'Premium Fragrances',
        handler: async (response) => {
          try {
            await axios.post(`${API}/orders/verify-payment`, {
              order_id: order.id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });

            // Record coupon usage
            if (appliedCoupon?.coupon_id) {
              try {
                await axios.post(`${API}/coupons/${appliedCoupon.coupon_id}/use`, {
                  customer_email: formData.customer_email,
                });
              } catch (_) { /* non-blocking */ }
            }

            toast.success('Payment successful! Order confirmed.');
            onClearCart();
            navigate(`/track-order?order_id=${order.id}&email=${encodeURIComponent(formData.customer_email)}`);
          } catch (error) {
            console.error('Payment verification error:', error);
            toast.error('Payment verification failed');
          }
        },
        prefill: {
          name: formData.customer_name,
          email: formData.customer_email,
          contact: formData.customer_phone,
        },
        theme: {
          color: '#D4AF37',
        },
      };

      if (window.Razorpay) {
        const razorpay = new window.Razorpay(options);
        razorpay.open();
      } else {
        toast.error('Payment gateway not loaded');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Checkout failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (cart.length === 0) {
    return (
      <div className="min-h-screen pt-32 pb-24">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <h1 className="font-display text-4xl md:text-5xl mb-4">Your cart is <span className="italic">empty</span></h1>
          <p className="text-muted-foreground mb-8">Add some fragrances before checking out.</p>
          <button
            onClick={() => navigate('/products')}
            className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8 py-4 uppercase tracking-widest text-sm font-medium transition-all"
          >
            Browse Products
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="checkout-page" className="min-h-screen pt-32 pb-24">
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="font-display text-5xl md:text-6xl font-normal tracking-tight text-foreground mb-6">
            <span className="italic">Checkout</span>
          </h1>

          <div className="border border-primary/20 bg-primary/5 px-5 py-4 mb-10 text-sm flex items-start gap-3">
            <Receipt size={18} className="text-primary flex-shrink-0 mt-0.5" strokeWidth={1.5} />
            <div>
              <p className="font-medium text-foreground">All prices are inclusive of GST</p>
              <p className="text-muted-foreground text-xs mt-1">
                Your full contact details — name, email and mobile — are required so we can send you order updates and
                a formal invoice separately after dispatch. You can always check status at <span className="text-primary">/track-order</span>.
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit} data-testid="checkout-form" className="space-y-12">
            <div className="space-y-6">
              <h2 className="font-display text-2xl mb-6">Contact Information</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="text-sm uppercase tracking-widest font-medium block mb-3">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="customer_name"
                    value={formData.customer_name}
                    onChange={handleChange}
                    required
                    data-testid="checkout-name-input"
                    className="w-full bg-transparent border-b border-white/20 rounded-none px-0 py-4 focus:border-primary focus:ring-0 placeholder:text-white/30 text-lg transition-colors duration-300 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="text-sm uppercase tracking-widest font-medium block mb-3">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="customer_email"
                    value={formData.customer_email}
                    onChange={handleChange}
                    required
                    data-testid="checkout-email-input"
                    className="w-full bg-transparent border-b border-white/20 rounded-none px-0 py-4 focus:border-primary focus:ring-0 placeholder:text-white/30 text-lg transition-colors duration-300 focus:outline-none"
                  />
                  <p className="text-[11px] text-muted-foreground mt-1">Used for order confirmation, invoice & tracking updates.</p>
                </div>

                <div>
                  <label className="text-sm uppercase tracking-widest font-medium block mb-3">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    name="customer_phone"
                    value={formData.customer_phone}
                    onChange={handleChange}
                    required
                    pattern="[6-9][0-9]{9}"
                    maxLength={10}
                    placeholder="10-digit mobile"
                    data-testid="checkout-phone-input"
                    className="w-full bg-transparent border-b border-white/20 rounded-none px-0 py-4 focus:border-primary focus:ring-0 placeholder:text-white/30 text-lg transition-colors duration-300 focus:outline-none"
                  />
                  <p className="text-[11px] text-muted-foreground mt-1">Courier & our team may call on this number for delivery.</p>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <h2 className="font-display text-2xl mb-6 flex items-center space-x-2">
                <Package size={24} className="text-primary" strokeWidth={1.5} />
                <span>Shipping Address</span>
              </h2>
              
              <div>
                <label className="text-sm uppercase tracking-widest font-medium block mb-3">
                  Full Address *
                </label>
                <textarea
                  name="shipping_address"
                  value={formData.shipping_address}
                  onChange={handleChange}
                  required
                  rows={3}
                  data-testid="checkout-address-input"
                  className="w-full bg-transparent border border-white/20 px-4 py-4 focus:border-primary focus:ring-0 placeholder:text-white/30 text-lg transition-colors duration-300 focus:outline-none resize-none"
                  placeholder="House/Flat No, Street, Landmark"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="text-sm uppercase tracking-widest font-medium block mb-3">
                    City *
                  </label>
                  <input
                    type="text"
                    name="shipping_city"
                    value={formData.shipping_city}
                    onChange={handleChange}
                    required
                    data-testid="checkout-city-input"
                    className="w-full bg-transparent border-b border-white/20 rounded-none px-0 py-4 focus:border-primary focus:ring-0 placeholder:text-white/30 text-lg transition-colors duration-300 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="text-sm uppercase tracking-widest font-medium block mb-3">
                    State *
                  </label>
                  <input
                    type="text"
                    name="shipping_state"
                    value={formData.shipping_state}
                    onChange={handleChange}
                    required
                    data-testid="checkout-state-input"
                    className="w-full bg-transparent border-b border-white/20 rounded-none px-0 py-4 focus:border-primary focus:ring-0 placeholder:text-white/30 text-lg transition-colors duration-300 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="text-sm uppercase tracking-widest font-medium block mb-3">
                    Pincode *
                  </label>
                  <input
                    type="text"
                    name="shipping_pincode"
                    value={formData.shipping_pincode}
                    onChange={handleChange}
                    required
                    maxLength={6}
                    pattern="[0-9]{6}"
                    data-testid="checkout-pincode-input"
                    className="w-full bg-transparent border-b border-white/20 rounded-none px-0 py-4 focus:border-primary focus:ring-0 placeholder:text-white/30 text-lg transition-colors duration-300 focus:outline-none"
                    placeholder="6-digit pincode"
                  />
                </div>
              </div>
            </div>

            {shippingRates.length > 0 && paymentMethod !== 'pickup' && (
              <div className="space-y-6">
                <h2 className="font-display text-2xl mb-6 flex items-center space-x-2">
                  <Truck size={24} className="text-primary" strokeWidth={1.5} />
                  <span>Shipping Method</span>
                </h2>
                
                <div className="space-y-3">
                  {shippingRates.map((rate, index) => (
                    <div
                      key={index}
                      onClick={() => setSelectedShipping(rate)}
                      className={`p-6 border cursor-pointer transition-all duration-300 ${ selectedShipping === rate
                          ? 'border-primary bg-primary/5'
                          : 'border-white/10 hover:border-primary/50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center space-x-3">
                            <input
                              type="radio"
                              checked={selectedShipping === rate}
                              onChange={() => setSelectedShipping(rate)}
                              className="w-4 h-4"
                            />
                            <div>
                              <p className="font-medium">{rate.carrier}</p>
                              <p className="text-sm text-muted-foreground">
                                {rate.service_type} • Delivery in {rate.estimated_days} days
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-xl font-accent italic text-primary">
                            {rate.rate === 0 ? 'FREE' : `₹${rate.rate.toFixed(2)}`}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="border border-white/10 p-8 space-y-4">
              <h2 className="font-display text-2xl mb-6">Order Summary</h2>

              <div className="space-y-3">
                {cart.map((item) => (
                  <div key={item.cartItemId} className="flex justify-between text-sm">
                    <span className="text-muted-foreground">
                      {item.displayName || item.name} x {item.quantity}
                    </span>
                    <span>₹{(item.price * item.quantity).toFixed(2)}</span>
                  </div>
                ))}
              </div>

              {/* Coupon section */}
              <div className="border-t border-white/10 pt-4">
                <label className="text-xs uppercase tracking-widest font-medium block mb-3 text-muted-foreground flex items-center gap-2">
                  <Tag size={14} strokeWidth={1.5} /> Have a coupon?
                </label>
                {appliedCoupon ? (
                  <div className="flex items-center justify-between border border-green-500/30 bg-green-500/5 px-4 py-3" data-testid="applied-coupon">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 size={16} className="text-green-400" strokeWidth={1.5} />
                      <span className="font-mono text-primary tracking-wider">{appliedCoupon.code}</span>
                      <span className="text-xs text-muted-foreground">applied</span>
                    </div>
                    <button type="button" onClick={handleRemoveCoupon} className="text-muted-foreground hover:text-destructive" data-testid="remove-coupon-button">
                      <X size={16} strokeWidth={1.5} />
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                      placeholder="Enter code"
                      data-testid="coupon-input"
                      className="flex-1 bg-transparent border-b border-white/20 px-0 py-2 focus:border-primary focus:outline-none text-base uppercase tracking-wider"
                    />
                    <button
                      type="button"
                      onClick={handleApplyCoupon}
                      disabled={couponLoading}
                      data-testid="apply-coupon-button"
                      className="text-xs uppercase tracking-widest px-4 border border-primary/30 text-primary hover:bg-primary/10 transition-colors disabled:opacity-50"
                    >
                      {couponLoading ? 'Applying...' : 'Apply'}
                    </button>
                  </div>
                )}
              </div>

              <div className="border-t border-white/10 pt-4 space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span>₹{subtotal.toFixed(2)}</span>
                </div>
                {appliedCoupon && discount > 0 && (
                  <div className="flex justify-between text-sm text-green-400" data-testid="discount-row">
                    <span>Discount ({appliedCoupon.code})</span>
                    <span>− ₹{discount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Shipping</span>
                  <span data-testid="shipping-cost">
                    {isPickup ? <span className="text-primary">Pickup / Manual</span> :
                      loadingRates ? 'Calculating...' : (
                      appliedCoupon?.free_shipping && rawShippingCost > 0 ? (
                        <span><span className="line-through text-muted-foreground mr-2">₹{rawShippingCost.toFixed(2)}</span><span className="text-green-400">FREE</span></span>
                      ) : shippingCost === 0 ? 'Free' : `₹${shippingCost.toFixed(2)}`
                    )}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Weight</span>
                  <span>{totalWeight}g</span>
                </div>
              </div>

              <div className="border-t border-white/10 pt-4 flex justify-between text-lg font-medium">
                <span>Total</span>
                <span className="font-accent text-2xl italic text-primary" data-testid="checkout-total">
                  ₹{total.toFixed(2)}
                </span>
              </div>
            </div>

            {/* Payment method picker */}
            <div className="border border-white/10 p-6 space-y-3">
              <h3 className="text-xs uppercase tracking-widest font-medium text-muted-foreground mb-3">Payment Method</h3>
              <label className={`flex items-start gap-3 p-4 border cursor-pointer transition-colors ${paymentMethod === 'razorpay' ? 'border-primary bg-primary/5' : 'border-white/10 hover:border-primary/40'}`}>
                <input
                  type="radio"
                  name="payment_method"
                  value="razorpay"
                  checked={paymentMethod === 'razorpay'}
                  onChange={() => setPaymentMethod('razorpay')}
                  data-testid="payment-razorpay"
                  className="mt-1"
                />
                <div>
                  <p className="font-medium">Pay Online (Razorpay)</p>
                  <p className="text-xs text-muted-foreground mt-1">Cards · UPI · Netbanking · Wallets · EMI</p>
                </div>
              </label>
              <label className={`flex items-start gap-3 p-4 border cursor-pointer transition-colors ${paymentMethod === 'cod' ? 'border-primary bg-primary/5' : 'border-white/10 hover:border-primary/40'}`}>
                <input
                  type="radio"
                  name="payment_method"
                  value="cod"
                  checked={paymentMethod === 'cod'}
                  onChange={() => setPaymentMethod('cod')}
                  data-testid="payment-cod"
                  className="mt-1"
                />
                <div>
                  <p className="font-medium">Cash on Delivery (BlueDart)</p>
                  <p className="text-xs text-muted-foreground mt-1">Pay cash when the BlueDart courier delivers. Available on all serviceable pincodes.</p>
                </div>
              </label>
              <label className={`flex items-start gap-3 p-4 border cursor-pointer transition-colors ${paymentMethod === 'pickup' ? 'border-primary bg-primary/5' : 'border-white/10 hover:border-primary/40'}`}>
                <input
                  type="radio"
                  name="payment_method"
                  value="pickup"
                  checked={paymentMethod === 'pickup'}
                  onChange={() => setPaymentMethod('pickup')}
                  data-testid="payment-pickup"
                  className="mt-1"
                />
                <div>
                  <p className="font-medium">In-Person Pickup / Courier of Your Choice</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Collect from our Kannauj office, or we'll ship via your preferred courier. Pay us directly via UPI QR / bank
                    transfer once the shipment is booked. <span className="text-primary">No shipping charges at checkout.</span>
                  </p>
                </div>
              </label>
            </div>

            <button
              type="submit"
              disabled={loading || (loadingRates && paymentMethod !== 'pickup') || (!selectedShipping && paymentMethod !== 'pickup')}
              data-testid="place-order-button"
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8 py-4 uppercase tracking-widest text-sm font-medium transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Processing...'
                : paymentMethod === 'pickup' ? 'Place Order (Pickup / Manual Courier)'
                : paymentMethod === 'cod' ? 'Place Order (Cash on Delivery)'
                : loadingRates ? 'Loading Shipping...' : 'Place Order & Pay'}
            </button>
          </form>
        </motion.div>
      </div>
    </div>
  );
};
